import { PostHog } from "posthog-js";
import { useQuery } from "react-query";

import {
  makeSimpleGetRequest,
  makeSimplePatchRequest,
  makeSimplePostRequest,
} from "lib/axios";
import { ORDERBOOK_URL } from "services/constants";
import { Offer } from "services/offers/types";

import { InstantOffer, ListingOffer, OfferPayload } from "./types";

export const makeOffer = async (
  listingId: number,
  payload: OfferPayload,
  posthog: PostHog,
): Promise<ListingOffer> => {
  const response = await makeSimplePostRequest<ListingOffer>(
    `${ORDERBOOK_URL}/listings/${listingId}/offers/`,
    JSON.stringify(payload),
    [201],
  );
  posthog?.capture("Placed Listing Offer");
  return response;
};

export const increaseOffer = async (
  listingId: number,
  payload: OfferPayload,
  posthog: PostHog,
): Promise<ListingOffer> => {
  const response = await makeSimplePostRequest<ListingOffer>(
    `${ORDERBOOK_URL}/listings/${listingId}/offers/`,
    JSON.stringify(payload),
    [200, 201],
  );
  posthog?.capture("Edit Listing Offer");
  return response;
};

export const makeCounteroffer = async (
  listingId: number,
  offerId: number | string,
  payload: OfferPayload,
  posthog: PostHog,
): Promise<ListingOffer> => {
  const response = await makeSimplePostRequest<ListingOffer>(
    `${ORDERBOOK_URL}/listings/${listingId}/offers/${offerId}/counter/`,
    JSON.stringify(payload),
    [200, 201],
  );
  posthog?.capture("Placed Listing Counteroffer");
  return response;
};

export const withdrawOffer = async (
  listingId: number,
  offerId: number,
  posthog: PostHog,
) => {
  const response = await makeSimplePatchRequest<ListingOffer>(
    `${ORDERBOOK_URL}/listings/${listingId}/offers/${offerId}/`,
    JSON.stringify({ state: "fail_offer_withdrawn" }),
    [200],
  );
  posthog?.capture("Buyer offer withdrawn.");
  return response;
};

export const acceptListingOffer = async (
  offer: ListingOffer,
  listingId: number,
  posthog: PostHog,
): Promise<void> => {
  await makeSimplePatchRequest(
    `${ORDERBOOK_URL}/listings/${listingId}/offers/${offer.id}/`,
    JSON.stringify({ state: "awaiting_due_diligence" }),
    [200],
  );
  posthog?.capture("Accepted Listing Offer", {
    amount: offer.amount,
    listing_id: listingId,
  });
};

export const rejectListingOffer = async (
  offer: ListingOffer,
  listingId: number,
  posthog: PostHog,
): Promise<void> => {
  await makeSimplePatchRequest(
    `${ORDERBOOK_URL}/listings/${listingId}/offers/${offer.id}/`,
    JSON.stringify({ state: "fail_offer_rejected" }),
    [200],
  );
  posthog?.capture("Declined Listing Offer", {
    amount: offer.amount,
    listing_id: listingId,
  });
};

export const getOffer = async (offerId: number): Promise<Offer> =>
  await makeSimpleGetRequest<Offer>(`${ORDERBOOK_URL}/offers/${offerId}/`);

export const useOffer = (offerId: number) => {
  return useQuery<Offer>(["offer", offerId], () => getOffer(offerId), {
    enabled: !!offerId,
    retry: false,
  });
};

export const getInstantOffer = async (offerId: number): Promise<InstantOffer> =>
  await makeSimpleGetRequest<InstantOffer>(
    `${ORDERBOOK_URL}/instant-offers/${offerId}`,
  );

export const useManyOffers = (queryString?: string) => {
  return useQuery("offers", () =>
    makeSimpleGetRequest<Offer[]>(
      `${ORDERBOOK_URL}/offers/${queryString ? queryString : ""}`,
    ),
  );
};
